// The builder's research pane (artboard 4, signed off 2026-08-28): the GWAS catalogue as the evidence,
// MedQuery as the citation check. The two empty states are artboard 7 and must never look alike.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Alert, Button, Input } from "antd";
import { BookOutlined, CheckOutlined, CloseOutlined, ExperimentOutlined, LockOutlined, PlusOutlined, SearchOutlined, WarningOutlined } from "@ant-design/icons";

import InlineLoading from "../../../../components/shared/InlineLoading";
import ResultRow from "../../../research/ResultRow";
import LiteratureArticleDrawer from "../../../research/LiteratureArticleDrawer";
import { useNotifications } from "../../../../context/NotificationContext";
import { attachPanelSnps } from "../../../../api/geneCodes/PanelApi";
import { fetchVariantCatalogue } from "../../../../api/geneCodes/VariantCatalogueApi";
import { readLiteratureForTrait, LiteratureEvidence } from "../../../../api/research/literatureVerification";
import { LiteratureResult, ResearchRecord } from "../../../../types/research/ResearchTypes";
import { Panel } from "../../../../types/geneCodes/Panel";
import { Trait } from "../../../../types/geneCodes/TraitTypes";
import { CatalogueVariant } from "../../../../types/geneCodes/VariantCatalogueTypes";
import { ResearchCandidate, attachPayloadForVariant, buildResearchCandidates, resolvedTerms } from "./researchCandidates";
import { formatPValue, subPhenotypeFrom } from "../../../../utils/geneCodes/pValue";
import "./researchPane.css";

export const RESEARCH_TASK = "research";

const PER_PAGE = 25;

const LITERATURE_PREVIEW = 5;

interface WheelResearchPaneProps {
	task: string;
	panel: Panel | null;
	trait: Trait | null;
	onClose: () => void;
	onAttached: () => void;
}

const errorsFrom = (error: unknown): string => {
	if (axios.isAxiosError(error) && error.response) {
		const data = error.response.data;
		if (data?.errors) return (Object.values(data.errors).flat() as string[]).join(" ");
		if (data?.message) return data.message;
	}
	if (error instanceof Error && error.message) return error.message;
	return "An unknown error occurred.";
};

const variantKey = (variant: CatalogueVariant, index: number) => `${index}:${variant.rs ?? "?"}`;

// Two names then "et al." — enough to recognise a paper, never a full author dump.
const attributionOf = (result: LiteratureResult): string | null => {
	const authors = Array.isArray(result.meta?.authors) ? result.meta.authors : [];
	if (authors.length === 0) return null;
	if (authors.length === 1) return authors[0];
	if (authors.length === 2) return `${authors[0]}, ${authors[1]}`;
	return `${authors[0]}, ${authors[1]}, et al.`;
};

const metaLineOf = (result: LiteratureResult): string | null => {
	const parts = [result.meta?.journal ?? null, result.meta?.pub_year != null ? String(result.meta.pub_year) : null, result.meta?.pmid ? `PMID ${result.meta.pmid}` : null].filter(Boolean) as string[];
	return parts.length > 0 ? parts.join(" · ") : null;
};

const toRecord = (result: LiteratureResult): ResearchRecord => ({
	...result,
	meta: result.meta as Record<string, unknown>,
	source_type: "literature",
	source_category: "medical_literature",
});

const CandidateCard: React.FC<{
	candidate: ResearchCandidate;
	added: boolean;
	busy: boolean;
	literatureAvailable: boolean;
	onAdd: () => void;
}> = ({ candidate, added, busy, literatureAvailable, onAdd }) => {
	const { variant } = candidate;
	const citations = variant.pmids.length;
	const gene = variant.gene ?? "";
	const riskAllele = variant.risk_allele ?? "";
	// One quiet line an author can scan for usability, rather than an em dash per absent field.
	const gaps = [gene === "" ? "gene not assigned" : null, riskAllele === "" ? "no risk allele recorded" : null].filter(Boolean) as string[];

	return (
		<div
			className={`mg-research__card${added ? " mg-research__card--added" : ""}${candidate.hidden ? " mg-research__card--unverified" : ""}`}
			data-testid="research-candidate"
			data-rs={variant.rs ?? ""}
			data-unverified={candidate.hidden ? "true" : undefined}
		>
			{candidate.hidden && (
				<span className="mg-research__unverified-flag" data-testid="research-unverified-flag">
					<WarningOutlined /> Unverified — no citation confirmed against PubMed
				</span>
			)}

			<div className="mg-research__card-head">
				<span className="mg-research__rs">{variant.rs ?? "—"}</span>
				{gene !== "" && <span className="mg-research__gene">{gene}</span>}
				{riskAllele !== "" && (
					<span className="mg-research__risk">
						risk <b>{riskAllele}</b>
					</span>
				)}
				{added ? (
					<span className="mg-research__added" data-testid="research-added">
						<CheckOutlined /> Added
					</span>
				) : (
					<Button size="small" icon={<PlusOutlined />} loading={busy} onClick={onAdd} data-testid="research-add">
						Add
					</Button>
				)}
			</div>

			{/* sample_size and population are hard-coded null by the catalogue controller — a metric that can never carry a value is noise. */}
			<div className="mg-research__metrics">
				<span className="mg-research__metric" data-testid="research-or">
					OR {variant.or_beta ?? "—"}
				</span>
				{/* P_VALUE is a double live (5.4e-21 stores fine) — render IT; p_value_text is a sub-phenotype annotation, NA on most rows. */}
				<span className="mg-research__metric" data-testid="research-p">
					p {formatPValue(variant.p_value) ?? "—"}
				</span>
			</div>

			{gaps.length > 0 && (
				<span className="mg-research__gaps" data-testid="research-gaps">
					{gaps.join(" · ")}
				</span>
			)}

			{subPhenotypeFrom(variant.p_value_text) && (
				<span className="mg-research__subphenotype" data-testid="research-subphenotype">
					{subPhenotypeFrom(variant.p_value_text)}
				</span>
			)}

			{candidate.otherPanels.length > 0 && (
				<span className="mg-research__membership" data-testid="research-membership">
					Also in {candidate.otherPanels.join(", ")}
				</span>
			)}

			{literatureAvailable ? (
				<span className={`mg-research__citations${candidate.unverified.length > 0 ? " mg-research__citations--partial" : ""}`} data-testid="research-citations">
					{candidate.unverified.length > 0 ? <WarningOutlined /> : <CheckOutlined />}
					{candidate.hidden
						? `${citations} ${citations === 1 ? "citation" : "citations"}, none verified`
						: candidate.unverified.length > 0
							? `${citations} citations, ${candidate.unverified.length} unverified — hidden`
							: `${candidate.verified.length} of ${citations} citations verified`}
				</span>
			) : (
				<span className="mg-research__citations mg-research__citations--unchecked" data-testid="research-citations-unchecked">
					{citations} {citations === 1 ? "citation" : "citations"}, not checked
				</span>
			)}
		</div>
	);
};

const WheelResearchPane: React.FC<WheelResearchPaneProps> = ({ task, panel, trait, onClose, onAttached }) => {
	const { showSuccess, showError } = useNotifications();

	const open = task === RESEARCH_TASK && Boolean(panel);

	const [term, setTerm] = useState("");
	// Bound at open, not read live: an add refreshes the wheel, which clears the dashboard's selected
	// trait — a live read would blank the pane's results and attach the next SNP under no trait at all.
	const [activeTrait, setActiveTrait] = useState<string | null>(null);
	const [searched, setSearched] = useState("");
	const [loading, setLoading] = useState(false);
	const [variants, setVariants] = useState<CatalogueVariant[]>([]);
	const [total, setTotal] = useState(0);
	const [catalogueError, setCatalogueError] = useState("");
	const [literature, setLiterature] = useState<LiteratureEvidence | null>(null);
	const [noticeDismissed, setNoticeDismissed] = useState(false);
	const [showUnverified, setShowUnverified] = useState(false);
	const [showAllLiterature, setShowAllLiterature] = useState(false);
	const [openRecord, setOpenRecord] = useState<ResearchRecord | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [added, setAdded] = useState<string[]>([]);
	const [attaching, setAttaching] = useState("");

	const reset = useCallback(() => {
		setSearched("");
		setVariants([]);
		setTotal(0);
		setCatalogueError("");
		setLiterature(null);
		setNoticeDismissed(false);
		setShowUnverified(false);
		setShowAllLiterature(false);
		setAdded([]);
	}, []);

	const wasOpen = useRef(false);

	useEffect(() => {
		if (open && !wasOpen.current) {
			const opened = trait?.trait_name ?? null;
			setActiveTrait(opened);
			setTerm(opened ?? panel?.name_panel ?? "");
			reset();
		}
		wasOpen.current = open;
	}, [open, trait, panel, reset]);

	const runSearch = async () => {
		const query = term.trim();
		// The endpoint 422s without a selective filter, so an empty box must not reach it at all.
		if (query === "" || !panel) return;

		setLoading(true);
		setCatalogueError("");
		setNoticeDismissed(false);
		setShowUnverified(false);
		setShowAllLiterature(false);
		try {
			const page = await fetchVariantCatalogue({ trait: query, panel_id: panel.id_panels, per_page: PER_PAGE });
			setVariants(page.variants);
			setTotal(page.total);
		} catch (error) {
			setVariants([]);
			setTotal(0);
			setCatalogueError(errorsFrom(error));
		} finally {
			setSearched(query);
			setLoading(false);
		}

		setLiterature(await readLiteratureForTrait(query));
	};

	const literatureAvailable = literature?.status === "ok";

	const candidates = useMemo(
		() => buildResearchCandidates(variants, literature !== null && literature.status === "ok" ? literature.pmids : null, panel?.name_panel ?? null),
		[variants, literature, panel],
	);

	const shown = candidates.filter((candidate) => !candidate.hidden);
	const hiddenCount = candidates.length - shown.length;
	const visible = showUnverified ? candidates : shown;

	const literatureResults = literature !== null && literature.status === "ok" ? literature.results ?? [] : [];
	const literatureTotal = literature !== null && literature.status === "ok" ? literature.total : 0;
	const visibleLiterature = showAllLiterature ? literatureResults : literatureResults.slice(0, LITERATURE_PREVIEW);

	const efoTerms = useMemo(() => resolvedTerms(variants, "efo"), [variants]);
	const meshTerms = useMemo(() => resolvedTerms(variants, "mesh"), [variants]);

	const openArticle = (result: LiteratureResult) => {
		setOpenRecord(toRecord(result));
		setDrawerOpen(true);
	};

	const handleAdd = async (candidate: ResearchCandidate) => {
		const rs = candidate.variant.rs;
		if (!panel || !rs) return;

		setAttaching(rs);
		try {
			const result = await attachPanelSnps(panel.id_panels, [attachPayloadForVariant(candidate.variant, activeTrait)]);
			// The server is the authority on what already existed, so its counts are what the toast says.
			const detail = result.skipped_count > 0 ? "Already in this wheel and skipped." : undefined;
			showSuccess(`Added ${rs} to ${panel.name_panel}.`, detail);
			setAdded((previous) => (previous.includes(rs) ? previous : [...previous, rs]));
			onAttached();
		} catch (error) {
			showError(`Could not add ${rs} to this wheel.`, errorsFrom(error));
		} finally {
			setAttaching("");
		}
	};

	if (!open || !panel) return null;

	const crumb = [panel.name_panel, activeTrait].filter(Boolean).join(" › ");

	return (
		<div className="mg-research" data-testid="wheel-research-pane">
			<div className="mg-research__head">
				<div className="mg-research__title-row">
					<BookOutlined />
					<span className="mg-research__title">Research</span>
					<span className="mg-research__crumb">{crumb}</span>
					<Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} aria-label="Close research" data-testid="research-close" />
				</div>

				<Input
					value={term}
					onChange={(event) => setTerm(event.target.value)}
					onPressEnter={runSearch}
					placeholder="Search a phenotype or trait"
					aria-label="Search the variant catalogue"
					data-testid="research-search"
					suffix={<Button type="text" size="small" icon={<SearchOutlined />} onClick={runSearch} aria-label="Search" data-testid="research-search-go" />}
				/>

				{(efoTerms.length > 0 || meshTerms.length > 0) && (
					<div className="mg-research__ontology" data-testid="research-ontology">
						<span className="mg-research__ontology-label">Resolved to</span>
						{efoTerms.slice(0, 3).map((name) => (
							<span key={`efo-${name}`} className="mg-research__chip">
								<span className="mg-research__chip-key">EFO</span>
								{name}
							</span>
						))}
						{meshTerms.slice(0, 3).map((name) => (
							<span key={`mesh-${name}`} className="mg-research__chip">
								<span className="mg-research__chip-key">MeSH</span>
								{name}
							</span>
						))}
					</div>
				)}

				<div className="mg-research__sources" data-testid="research-sources">
					<span className="mg-research__source">
						<ExperimentOutlined /> GWAS catalogue · local
					</span>
					<span>·</span>
					<span className={`mg-research__source${literatureAvailable ? " mg-research__source--verified" : " mg-research__source--unchecked"}`} data-testid="research-source-literature">
						{literatureAvailable ? <CheckOutlined /> : <LockOutlined />}
						{literatureAvailable ? "Verified via MedQuery" : "Citations not verified"}
					</span>
				</div>
			</div>

			<div className="mg-research__body">
				{catalogueError !== "" && <Alert type="error" showIcon message="The variant catalogue could not be read" description={catalogueError} data-testid="research-catalogue-error" />}

				{/* Never rendered as an empty result set: "we could not check" and "there is nothing" lead to opposite conclusions. */}
				{literature !== null && literature.status !== "ok" && !noticeDismissed && (
					<div className="mg-research__state mg-research__state--unavailable" role="status" data-testid="research-unavailable" data-reason={literature.status}>
						<span className="mg-research__state-icon">
							<LockOutlined />
						</span>
						<span className="mg-research__state-title">Literature verification unavailable</span>
						<span className="mg-research__state-body">{literature.message}</span>
						<Button size="small" onClick={() => setNoticeDismissed(true)} data-testid="research-catalogue-only">
							Show catalogue only
						</Button>
					</div>
				)}

				{loading && <InlineLoading message="Searching the catalogue…" minHeight={120} />}

				{!loading && searched !== "" && variants.length === 0 && catalogueError === "" && (
					<div className="mg-research__state mg-research__state--empty" role="status" data-testid="research-no-results">
						<span className="mg-research__state-icon">
							<SearchOutlined />
						</span>
						<span className="mg-research__state-title">No variants for this search</span>
						<span className="mg-research__state-body">The GWAS catalogue has no association for “{searched}”. Try a broader phenotype or a MeSH term.</span>
						<Button
							size="small"
							onClick={() => {
								setTerm("");
								reset();
							}}
							data-testid="research-clear"
						>
							Clear search
						</Button>
					</div>
				)}

				{/* Keyed on candidates, not on `shown`: an all-hidden search still has a body to render. */}
				{!loading && candidates.length > 0 && (
					<>
						<div className="mg-research__eyebrow-row">
							<span className="mg-research__eyebrow">Candidate SNPs</span>
							<span className="mg-research__count" data-testid="research-count">
								{visible.length} shown{hiddenCount > 0 ? ` · ${hiddenCount} ${showUnverified ? "unverified" : "hidden"}` : ""}
								{total > candidates.length ? ` · ${total} matched` : ""}
							</span>
						</div>

						{visible.map((candidate, index) => (
							<CandidateCard
								key={variantKey(candidate.variant, index)}
								candidate={candidate}
								added={candidate.variant.membership.in_current_panel || (candidate.variant.rs !== null && added.includes(candidate.variant.rs))}
								busy={attaching === candidate.variant.rs}
								literatureAvailable={literatureAvailable}
								onAdd={() => handleAdd(candidate)}
							/>
						))}

						{hiddenCount > 0 && (
							<div className="mg-research__note" data-testid="research-hidden-note">
								<WarningOutlined />
								<span>
									{showUnverified
										? `${hiddenCount} unverified ${hiddenCount === 1 ? "candidate is" : "candidates are"} shown. None of their citations could be confirmed against PubMed — treat them as unconfirmed evidence.`
										: `${hiddenCount} ${hiddenCount === 1 ? "candidate is" : "candidates are"} hidden because their citations could not be verified against PubMed. Unverified evidence is not shown by default.`}
								</span>
								<Button size="small" onClick={() => setShowUnverified((previous) => !previous)} data-testid="research-show-unverified">
									{showUnverified ? "Hide unverified candidates" : "Show unverified candidates"}
								</Button>
							</div>
						)}
					</>
				)}

				{!loading && literatureResults.length > 0 && (
					<div className="mg-research__literature" data-testid="research-literature">
						<div className="mg-research__eyebrow-row">
							<span className="mg-research__eyebrow">Literature</span>
							<span className="mg-research__count" data-testid="research-literature-total">
								{visibleLiterature.length} of {literatureTotal.toLocaleString()} publications
							</span>
						</div>

						<div className={`mg-research__literature-list${showAllLiterature ? " mg-research__literature-list--all" : ""}`}>
							{visibleLiterature.map((result) => (
								<ResultRow
									key={result.id}
									title={result.title}
									attribution={attributionOf(result)}
									meta={metaLineOf(result)}
									snippet={result.snippet}
									selected={openRecord?.id === result.id}
									onClick={() => openArticle(result)}
									data-testid={`research-literature-row-${result.id}`}
								/>
							))}
						</div>

						{literatureResults.length > visibleLiterature.length && (
							<Button size="small" onClick={() => setShowAllLiterature(true)} data-testid="research-literature-more">
								Show {literatureResults.length - visibleLiterature.length} more
							</Button>
						)}
					</div>
				)}
			</div>

			{/* Mounted on the first article opened: the drawer fetches with react-query, and the pane must not own that query until a reader asks for one. */}
			{openRecord !== null && <LiteratureArticleDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} record={openRecord} />}
		</div>
	);
};

export default WheelResearchPane;
